const BASE_URL = "/api";

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (res.status === 401) {
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, body.error ?? "Request failed");
  }

  // Handle metrics endpoint which returns plain text
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    return (await res.text()) as unknown as T;
  }

  return res.json();
}

// ── Auth ──

export const auth = {
  login: (password: string) =>
    request<{ success: boolean }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),

  logout: () =>
    request<{ success: boolean }>("/auth/logout", { method: "POST" }),

  checkSession: () =>
    request<{ authenticated: boolean }>("/auth/session"),
};

// ── System ──

export const system = {
  health: () =>
    request<{
      status: string;
      timestamp: string;
      tools: number;
      activeAgents: number;
      eventBus: string;
    }>("/system/health"),

  tools: () =>
    request<{ tools: import("./types").ToolDefinition[] }>("/system/tools"),

  connectors: () =>
    request<{ connectors: import("./types").Connector[] }>("/system/connectors"),
};

// ── Agents ──

export const agents = {
  list: (params?: { status?: string; zone?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.zone) qs.set("zone", params.zone);
    const query = qs.toString();
    return request<{ agents: import("./types").Agent[] }>(
      `/agents${query ? `?${query}` : ""}`,
    );
  },

  get: (id: string) =>
    request<{ agent: import("./types").Agent; recentAudit: import("./types").AuditEntry[] }>(
      `/agents/${id}`,
    ),

  pause: (id: string, reason?: string) =>
    request<{ success: boolean }>(`/agents/${id}/pause`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  resume: (id: string) =>
    request<{ success: boolean }>(`/agents/${id}/resume`, { method: "POST" }),

  retire: (id: string, reason?: string) =>
    request<{ success: boolean }>(`/agents/${id}/retire`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  approve: (id: string) =>
    request<unknown>(`/agents/${id}/approve`, { method: "POST" }),

  deploy: (id: string) =>
    request<unknown>(`/agents/${id}/deploy`, { method: "POST" }),
};

// ── Claude Auth ──

export const claudeAuth = {
  status: () =>
    request<{
      loggedIn: boolean;
      authMethod?: string;
    }>("/claude-auth/status"),

  start: () =>
    request<{ url: string; state: string }>("/claude-auth/start", {
      method: "POST",
    }),

  complete: (code: string) =>
    request<{ success: boolean; loggedIn: boolean }>("/claude-auth/complete", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  reset: () =>
    request<{ success: boolean }>("/claude-auth/reset", { method: "POST" }),
};

// ── Audit ──

export const audit = {
  list: (params?: {
    agent_id?: string;
    action?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.agent_id) qs.set("agent_id", params.agent_id);
    if (params?.action) qs.set("action", params.action);
    if (params?.from) qs.set("from", params.from);
    if (params?.to) qs.set("to", params.to);
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.offset) qs.set("offset", String(params.offset));
    const query = qs.toString();
    return request<{
      entries: import("./types").AuditEntry[];
      total: number;
      limit: number;
      offset: number;
    }>(`/audit${query ? `?${query}` : ""}`);
  },
};

// ── Chat ──

export const chat = {
  listConversations: () =>
    request<{ conversations: import("./types").ChatConversation[] }>("/chat/conversations"),

  createConversation: () =>
    request<import("./types").ChatConversation>("/chat/conversations", { method: "POST" }),

  getConversation: (id: string) =>
    request<import("./types").ChatConversation>(`/chat/conversations/${id}`),

  deleteConversation: (id: string) =>
    request<{ success: boolean }>(`/chat/conversations/${id}`, { method: "DELETE" }),

  sendMessage: (conversationId: string, content: string) =>
    request<import("./types").ChatMessageData>(`/chat/conversations/${conversationId}/messages`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),

  updateTitle: (conversationId: string, title: string) =>
    request<{ success: boolean }>(`/chat/conversations/${conversationId}/title`, {
      method: "POST",
      body: JSON.stringify({ title }),
    }),
};

// ── Artifacts ──

export const artifacts = {
  list: (category?: string) => {
    const qs = category ? `?category=${encodeURIComponent(category)}` : "";
    return request<{ artifacts: import("./types").Artifact[] }>(`/artifacts${qs}`);
  },

  categories: () =>
    request<{ categories: import("./types").ArtifactCategory[] }>("/artifacts/categories"),

  get: (id: string) =>
    request<import("./types").Artifact>(`/artifacts/${id}`),

  save: (data: {
    title: string;
    description?: string;
    category?: string;
    widgetType?: string;
    html: string;
    size?: { w: number; h: number };
  }) =>
    request<import("./types").Artifact>("/artifacts", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: { title?: string; description?: string; category?: string }) =>
    request<import("./types").Artifact>(`/artifacts/${id}/update`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<{ success: boolean }>(`/artifacts/${id}`, { method: "DELETE" }),
};

// ── Voice ──
// Browser voice (ConvAI) removed in Phase 4b. Phone calls go via LiveKit SIP.
// Config and status endpoints retained for the settings wizard.

/** A single voice pipeline timing event emitted by voice-engine and
 *  stored in audit_log.details with event_type = 'voice.timing'. */
export interface VoiceTimingEvent {
  stage: string;
  timestamp_ms: number;
  /** Provided only for stages that record a duration (e.g. llm.first_token,
   *  tool.result, tts.first_audio). Absent when no duration was captured. */
  latency_ms?: number;
  /** Free-form per-stage payload (e.g. endpoint_ms for user.final_transcript). */
  metadata?: Record<string, unknown>;
}

export const voice = {
  getConfig: () =>
    request<import("./types").VoiceConfig>("/voice/config"),

  saveConfig: (config: { apiKey: string; voiceId?: string; model?: string }) =>
    request<{ success: boolean; webhookSecret: string }>("/voice/config", {
      method: "POST",
      body: JSON.stringify(config),
    }),

  startWebCall: () =>
    request<{ url: string; token: string; room: string; identity: string }>(
      "/voice/web-call",
      { method: "POST" },
    ),

  /** Look up a voice_session by its LiveKit room name. Returns null until
   *  the voice-engine registers the session (typically <1s after dispatch). */
  getSessionByRoom: (roomName: string) =>
    request<{
      id: string;
      room_name: string;
      status: string;
      started_at: string;
      direction: string;
      channel: string | null;
      caller_number: string | null;
      callee_number: string | null;
    }>(`/voice/sessions/by-room/${encodeURIComponent(roomName)}`),

  /** Cursor-based event poll for a live call. since_ts is millis epoch. */
  pollSessionEvents: (sessionId: string, sinceTs: number) =>
    request<{
      events: Array<{
        id: number;
        ts_ms: number;
        action: string;
        tool: string | null;
        topic: string | null;
        result_status: string;
        duration_ms: number | null;
        details: Record<string, unknown>;
      }>;
      count: number;
      has_more: boolean;
      cursor_ts?: number;
    }>(
      `/voice/sessions/${encodeURIComponent(sessionId)}/events/poll?since_ts=${sinceTs}`,
    ),

  /** Fetch all voice.timing instrumentation events for a session,
   *  ordered by timestamp ASC.  Returns an empty array when no timing
   *  events have been emitted yet (call not started or Agent A not running). */
  getTimings: (sessionId: string) =>
    request<{ events: VoiceTimingEvent[] }>(
      `/voice/sessions/${encodeURIComponent(sessionId)}/timing`,
    ),
};

// ── Policy ──────────────────────────────────────────────────────────────────

export interface UserPreferences {
  id: string;
  outbound_hours_start: string;
  outbound_hours_end: string;
  outbound_local_tz: string;
  default_max_per_contact_per_day: number;
  default_max_per_contact_per_week: number;
  paused: boolean;
  paused_reason: string | null;
  default_recap_channel: "telegram" | "sms" | "email" | "none";
  recording_default_enabled: boolean;
  updated_at: string;
}

export interface DncEntry {
  id: string;
  phone_number: string | null;
  person_id: string | null;
  person_name: string | null;
  reason: string | null;
  source: "manual" | "tripwire" | "user_request" | "federal_dnc";
  created_at: string;
}

export interface PolicyOverride {
  id: string;
  action: string;
  params_json: Record<string, unknown>;
  reasons: string[] | null;
  requested_by: string;
  requested_at: string;
  expires_at: string;
  source_session_id: string | null;
}

export const policy = {
  getPreferences: () => request<UserPreferences>("/policy/preferences"),
  updatePreferences: (updates: Partial<UserPreferences>) =>
    request<UserPreferences>("/policy/preferences", {
      method: "POST",
      body: JSON.stringify(updates),
    }),

  listDnc: () => request<{ entries: DncEntry[] }>("/policy/dnc"),
  addDnc: (entry: { phone_number?: string; person_id?: string; reason?: string }) =>
    request<DncEntry>("/policy/dnc", {
      method: "POST",
      body: JSON.stringify(entry),
    }),
  removeDnc: (id: string) =>
    request<{ success: boolean }>(`/policy/dnc/${id}`, { method: "DELETE" }),

  listOverrides: () =>
    request<{ overrides: PolicyOverride[] }>("/policy/overrides"),
  approveOverride: (id: string) =>
    request<{ success: boolean }>(`/policy/overrides/${id}/approve`, {
      method: "POST",
    }),
  denyOverride: (id: string, reason?: string) =>
    request<{ success: boolean }>(`/policy/overrides/${id}/deny`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
};

// ── Setup ──

export interface SetupStatus {
  google: { status: "connected" | "needs_auth" | "not_configured"; email: string | null };
  voice: { status: "not_configured" | "configured" };
  email: { status: "not_configured" | "configured" };
  telegram: { status: "not_configured" | "connected" };
  llm: { status: "connected"; provider: string; model: string };
}

export const setup = {
  status: () => request<SetupStatus>("/system/setup-status"),

  googleStatus: () =>
    request<{ connected: boolean; email: string | null; connected_at: string | null }>("/oauth/google/status"),

  googleSaveConfig: (clientId: string, clientSecret: string) =>
    request<{ success: boolean }>("/oauth/google/config", {
      method: "POST",
      body: JSON.stringify({ clientId, clientSecret }),
    }),

  googleDisconnect: () =>
    request<{ success: boolean }>("/oauth/google/disconnect", { method: "POST" }),
};

// ── User data / account ──

export interface ExportJob {
  job_id: string;
  status: "pending" | "running" | "ready" | "failed" | "expired";
  requested_at: string;
  ready_at: string | null;
  expires_at: string | null;
  object_key: string | null;
  bytes: number | null;
  error_message: string | null;
  download_url: string | null;
}

export const userData = {
  requestExport: () =>
    request<{ job_id: string; status: string }>("/user/export", {
      method: "POST",
    }),

  getExportStatus: (jobId: string) =>
    request<ExportJob>(`/user/export-status/${encodeURIComponent(jobId)}`),

  deleteAccount: (reason?: string) =>
    request<{ status: string; deleted_at: string }>("/user/delete", {
      method: "POST",
      body: JSON.stringify({ confirm: "DELETE MY ACCOUNT", reason }),
    }),
};

// ── Chief of Staff ──

export const cos = {
  listConversations: () =>
    request<{ conversations: import("./types").Conversation[] }>("/cos/conversations"),

  createConversation: () =>
    request<import("./types").Conversation>("/cos/conversations", { method: "POST" }),

  getConversation: (id: string) =>
    request<import("./types").Conversation>(`/cos/conversations/${id}`),

  sendMessage: (conversationId: string, content: string) =>
    request<import("./types").ConversationMessage>(`/cos/conversations/${conversationId}/messages`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),

  getPlan: (id: string) =>
    request<import("./types").DeploymentPlan>(`/cos/plans/${id}`),

  approvePlan: (id: string) =>
    request<import("./types").DeploymentPlan>(`/cos/plans/${id}/approve`, { method: "POST" }),

  rejectPlan: (id: string) =>
    request<{ success: boolean }>(`/cos/plans/${id}/reject`, { method: "POST" }),

  executePlan: (id: string) =>
    request<import("./types").DeploymentPlan>(`/cos/plans/${id}/execute`, { method: "POST" }),
};
